import type {
  NotificationKind,
  NotificationPageDto,
  NotificationPreferenceUpdate,
  NotificationSettingsDto,
  ProfileDto,
  ProfileUpdate,
  UnreadCountDto,
} from "@eduleo/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSessionKey } from "../../app/session-sync";
import { apiRequest } from "../../lib/api-client";
import { useAuthStore } from "../../stores/auth-store";

/**
 * The signed-in team member's own settings: profile, notifications and their choices. Every
 * key starts with the person, like all cached data.
 */
export const settingsKeys = {
  profile: (s: string | null) => ["me", s, "profile"] as const,
  notifications: (s: string | null) => ["me", s, "notifications"] as const,
  unread: (s: string | null) => ["me", s, "notifications", "unread"] as const,
  feed: (s: string | null) => ["me", s, "notifications", "feed"] as const,
  notificationSettings: (s: string | null) => ["me", s, "notification-settings"] as const,
};

/** How often the bell asks for new ones while the page is open and in front. */
export const UNREAD_POLL_MS = 60_000;

export function useProfile() {
  const session = useSessionKey();
  return useQuery({
    queryKey: settingsKeys.profile(session),
    queryFn: ({ signal }) => apiRequest<ProfileDto>("/platform/profile", { signal }),
    enabled: Boolean(session),
  });
}

/** Saves your name and work email. The menu shows the new name at once. */
export function useSaveProfile() {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  return useMutation({
    mutationFn: (body: ProfileUpdate) =>
      apiRequest<ProfileDto>("/platform/profile", { method: "PATCH", body }),
    onSuccess: (profile) => {
      queryClient.setQueryData(settingsKeys.profile(session), profile);
      const user = useAuthStore.getState().user;
      if (user && user.id === profile.id) {
        useAuthStore.setState({ user: { ...user, displayName: profile.displayName } });
      }
      void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      // The email on Notifications comes from here.
      void queryClient.invalidateQueries({ queryKey: settingsKeys.notificationSettings(session) });
      // Team lists show names.
      void queryClient.invalidateQueries({ queryKey: ["platform", session, "team"] });
    },
  });
}

export function useUnreadCount(enabled: boolean) {
  const session = useSessionKey();
  return useQuery({
    queryKey: settingsKeys.unread(session),
    queryFn: ({ signal }) =>
      apiRequest<UnreadCountDto>("/platform/notifications/unread", { signal }),
    enabled: enabled && Boolean(session),
    refetchInterval: UNREAD_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

export function useNotifications(enabled: boolean) {
  const session = useSessionKey();
  return useInfiniteQuery({
    queryKey: settingsKeys.feed(session),
    queryFn: ({ pageParam, signal }) =>
      apiRequest<NotificationPageDto>("/platform/notifications", {
        signal,
        query: { cursor: pageParam || undefined },
      }),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: enabled && Boolean(session),
  });
}

/** Opening the list: everything in it is no longer new, and the bell drops to zero. */
export function useMarkSeen() {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  return useMutation({
    mutationFn: () =>
      apiRequest<UnreadCountDto>("/platform/notifications/seen", { method: "POST" }),
    onSuccess: (count) => queryClient.setQueryData(settingsKeys.unread(session), count),
  });
}

export function useNotificationSettings() {
  const session = useSessionKey();
  return useQuery({
    queryKey: settingsKeys.notificationSettings(session),
    queryFn: ({ signal }) =>
      apiRequest<NotificationSettingsDto>("/platform/notifications/settings", { signal }),
    enabled: Boolean(session),
  });
}

/**
 * Flips one kind's switches and saves at once. The switch moves straight away; if saving
 * fails it goes back and the page says so.
 */
export function useSaveNotificationPreference() {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  const key = settingsKeys.notificationSettings(session);
  return useMutation({
    mutationFn: ({ kind, ...body }: NotificationPreferenceUpdate & { kind: NotificationKind }) =>
      apiRequest<NotificationSettingsDto>(
        `/platform/notifications/settings/${encodeURIComponent(kind)}`,
        { method: "PUT", body },
      ),
    onMutate: async ({ kind, ...choice }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<NotificationSettingsDto>(key);
      if (previous) {
        queryClient.setQueryData<NotificationSettingsDto>(key, {
          ...previous,
          preferences: previous.preferences.map((preference) =>
            preference.kind === kind ? { ...preference, ...choice } : preference,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (settings) => {
      queryClient.setQueryData(key, settings);
      // Turning a kind off for the console changes what the bell counts.
      void queryClient.invalidateQueries({ queryKey: settingsKeys.notifications(session) });
    },
  });
}
